import React from 'react';
import { ResumeData } from './resumeTypes';
import { AppliedTemplate, formatDate, formatBirthDate, getLocalizedText } from './resumeUtils';
import { formatTextForDisplay, escapeHtml } from './textFormatter';

interface Template03Props {
  data: ResumeData;
  template: AppliedTemplate;
  accentColor: string;
  accentTextColor: string;
}

const Template03Renderer: React.FC<Template03Props> = React.memo(({
  data,
  template: t,
  accentColor,
  accentTextColor,
}) => {
  const txt = (key: string) => getLocalizedText(key, data.lang, t.translations);
  const sidebarText = t.colors.sidebarText || '#111827';
  const accentWithAlpha =
    accentColor.startsWith('#') ? accentColor + '66' : accentColor.startsWith('rgb')
      ? accentColor.replace(')', ', 0.4)').replace('rgb(', 'rgba(') : accentColor + '66';
  const sidebarBg = accentWithAlpha;
  const mainText = t.colors.text || '#111827';
  const mutedColor = t.colors.muted || '#9CA3AF';
  const borderColor = t.colors.border || '#E5E7EB';
  const baseFontSize = `${t.fonts.scale.base}px`;

  /* Заголовки без заливки, обводка цветом акцента шаблона */
  const outlineBorder = '1px solid ' + accentColor;
  /* Превью: симметричный padding для ровного выравнивания по центру */
  const titlePadding = '6px 12px';
  const SidebarTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h2 className="t03-section-title-outline" style={{
      background: 'transparent', color: sidebarText,
      padding: titlePadding, marginBottom: 10, fontSize: 14,
      fontWeight: 600, textTransform: 'uppercase', margin: '0 0 10px',
      border: outlineBorder, lineHeight: 1.2,
      display: 'flex', alignItems: 'center',
    }}>{children}</h2>
  );

  const MainTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <h2 className="t03-section-title-outline" style={{
      background: 'transparent', color: mainText,
      padding: titlePadding, marginBottom: 10, fontSize: 14,
      fontWeight: 600, textTransform: 'uppercase', margin: '0 0 10px',
      border: outlineBorder, lineHeight: 1.2,
      display: 'flex', alignItems: 'center',
    }}>{children}</h2>
  );

  const renderPhoto = () => {
    if (!data.photoUrl && !data.photo) return null;
    return (
      <div style={{ marginBottom: 12 }}>
        <div
          className="t03-photo-clip"
          style={{
            width: 150, height: 150,
            overflow: 'hidden', borderRadius: 12,
          }}
        >
          <img
            src={data.photo || data.photoUrl}
            alt="Фото"
            crossOrigin="anonymous"
            className="resume-cover-photo"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', display: 'block' }}
          />
        </div>
      </div>
    );
  };

  const renderIdentity = () => (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 20, fontWeight: 700, color: sidebarText, lineHeight: 1.2, marginBottom: 4 }}>
        {data.name?.last || ''} {data.name?.first || ''}
      </div>
      {data.position && (
        <div style={{ fontSize: baseFontSize, color: sidebarText, lineHeight: 1.3 }}>
          {data.position}
        </div>
      )}
    </div>
  );

  const renderContacts = () => {
    if (!data.contacts?.email && (!data.contacts?.phones || data.contacts.phones.length === 0)) return null;
    return (
      <div style={{ marginBottom: 14 }}>
        <SidebarTitle>{txt('contacts')}</SidebarTitle>
        <div style={{ fontSize: baseFontSize }}>
          {data.contacts?.phones?.map((phone, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, color: sidebarText }}>
              <span style={{ fontSize: 14 }}>📞</span>
              <span>{phone}</span>
            </div>
          ))}
          {data.contacts?.email && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, color: sidebarText }}>
              <span style={{ fontSize: 14 }}>📧</span>
              <span>{data.contacts.email}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSkills = () => {
    if (!data.skills || data.skills.length === 0) return null;
    return (
      <div style={{ marginBottom: 14 }}>
        <SidebarTitle>{txt('skills')}</SidebarTitle>
        <div className="t03-skills-list" style={{ fontSize: baseFontSize }}>
          {data.skills.map((skill, i) => (
            <div key={i} className="t03-skill-card" style={{ marginBottom: 8 }}>
              <div className="t03-skill-label" style={{ color: sidebarText, marginBottom: 4 }}>
                <span style={{ lineHeight: 1.2 }}>{skill}</span>
              </div>
              <div className="t03-skill-bar t03-skill-bar-full" style={{ display: 'flex', gap: 6, width: '100%', marginBottom: 2 }}>
                {[1, 2, 3, 4].map((d) => (
                  <span key={d} className="t03-bar-seg" style={{ flex: 1, height: 1, minHeight: 1, background: accentColor }} />
                ))}
              </div>
              <div style={{ color: mutedColor, fontSize: `${Math.max(t.fonts.scale.base - 2, 10)}px`, textAlign: 'right' }}>
                Профессиональный
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderLanguages = () => {
    if (!data.languages || data.languages.length === 0) return null;
    return (
      <div style={{ marginBottom: 14 }}>
        <SidebarTitle>{txt('languages')}</SidebarTitle>
        <div style={{ fontSize: baseFontSize }}>
          {data.languages.map((lang, i) => (
            <div key={i} style={{ marginBottom: 6 }}>
              <div style={{ fontWeight: 600, color: sidebarText }}>{lang.name}</div>
              <div style={{ color: sidebarText }}>{lang.level}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderMeta = () => {
    const items: Array<{ label: string; value: string }> = [];
    if (data.location?.city) items.push({ label: 'Город проживания', value: data.location.city });
    if (data.birthDate) items.push({ label: 'Дата рождения', value: formatBirthDate(data.birthDate) });
    if (data.meta?.citizenship) items.push({ label: 'Гражданство', value: data.meta.citizenship });
    if (data.meta?.employment) items.push({ label: 'Занятость', value: data.meta.employment });
    if (data.meta?.schedule) items.push({ label: 'График работы', value: data.meta.schedule });
    if (data.meta?.relocation) items.push({ label: 'Готов к переезду', value: 'Да' });
    if (data.meta?.travelReady) items.push({ label: 'Готов к командировкам', value: 'Да' });
    if (items.length === 0) return null;

    return (
      <div style={{ marginBottom: 14 }}>
        <SidebarTitle>ДОПОЛНИТЕЛЬНО</SidebarTitle>
        <div style={{ fontSize: baseFontSize }}>
          {items.map((item, i) => (
            <div key={i} style={{ marginBottom: 6 }}>
              <div style={{ color: sidebarText, fontWeight: 600 }}>{item.label}</div>
              <div style={{ color: sidebarText }}>{item.value}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSummary = () => {
    if (!data.about || data.about.trim() === '') return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <MainTitle>{txt('summary')}</MainTitle>
        <div
          className="t03-body-text"
          style={{ fontSize: baseFontSize, color: mainText, lineHeight: 1.5 }}
          dangerouslySetInnerHTML={{ __html: escapeHtml(data.about).replace(/\\n/g, '\n').replace(/\n/g, '<br>') }}
        />
      </div>
    );
  };

  const renderExperience = () => {
    if (!data.experience || data.experience.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <MainTitle>{txt('experience')}</MainTitle>
        {data.experience.map((exp, i) => (
          <div key={i} className="avoid-break" style={{ marginBottom: i < data.experience!.length - 1 ? 12 : 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontWeight: 600, color: mainText }}>{exp.company}</span>
              <span style={{ fontSize: baseFontSize, color: mutedColor, whiteSpace: 'nowrap', marginLeft: 8 }}>
                {exp.dateFrom ? formatDate(exp.dateFrom) : '—'} – {exp.dateTo ? formatDate(exp.dateTo) : 'н.в.'}
              </span>
            </div>
            <div style={{ fontStyle: 'italic', fontSize: baseFontSize, color: mainText, margin: '2px 0 4px' }}>
              {exp.title}
            </div>
            {exp.description && (
              <div
                className="t03-body-text"
                style={{ fontSize: baseFontSize, color: mainText }}
                dangerouslySetInnerHTML={{ __html: formatTextForDisplay(exp.description) }}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderEducation = () => {
    if (!data.education || data.education.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <MainTitle>{txt('education')}</MainTitle>
        {data.education.map((edu, i) => (
          <div key={i} className="avoid-break" style={{ marginBottom: i < data.education!.length - 1 ? 10 : 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontWeight: 600, color: mainText }}>
                {edu.org}{edu.specialty ? `, ${edu.specialty}` : ''}{edu.faculty ? `, ${edu.faculty}` : ''}
              </span>
              <span style={{ fontSize: baseFontSize, color: mutedColor, whiteSpace: 'nowrap', marginLeft: 8 }}>
                {edu.graduationYear}
              </span>
            </div>
            {edu.degree && (
              <div style={{ fontStyle: 'italic', fontSize: baseFontSize, color: mainText, marginTop: 2 }}>
                {edu.degree}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderCourses = () => {
    if (!data.courses || data.courses.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <MainTitle>{txt('courses')}</MainTitle>
        {data.courses.map((course, i) => (
          <div key={i} className="avoid-break" style={{ marginBottom: i < data.courses!.length - 1 ? 10 : 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <span style={{ fontWeight: 600, color: mainText }}>{course.name}</span>
              {course.year && (
                <span style={{ fontSize: baseFontSize, color: mutedColor, whiteSpace: 'nowrap', marginLeft: 8 }}>
                  {course.year}
                </span>
              )}
            </div>
            {course.organization && (
              <div style={{ fontSize: baseFontSize, color: mainText, marginTop: 2 }}>{course.organization}</div>
            )}
            {course.description && (
              <div className="t03-body-text" style={{ fontSize: baseFontSize, color: mainText, marginTop: 4 }}>
                {course.description}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderHobbies = () => {
    if (!data.hobbies || data.hobbies.length === 0) return null;
    return (
      <div style={{ marginBottom: 16 }}>
        <MainTitle>{txt('hobbies') === 'hobbies' ? 'УВЛЕЧЕНИЯ' : txt('hobbies')}</MainTitle>
        {data.hobbies.map((hobby, i) => {
          const name = typeof hobby === 'string' ? hobby : hobby.name;
          const desc = typeof hobby === 'string' ? undefined : hobby.description;
          return (
            <div key={i} className="avoid-break" style={{ marginBottom: 8 }}>
              <div style={{ fontWeight: 600, color: mainText, fontSize: baseFontSize }}>{name}</div>
              {desc && <div style={{ color: mainText, fontSize: baseFontSize }}>{desc}</div>}
            </div>
          );
        })}
      </div>
    );
  };

  const css = `
    .template-template-03 .t03-grid {
      display: grid;
      grid-template-columns: 220px 1fr;
      gap: 0;
      min-height: 100%;
      width: 100%;
    }
    .template-template-03 .t03-sidebar {
      background: ${sidebarBg};
      color: ${sidebarText};
      padding: 20px;
      overflow: visible;
      min-width: 0;
    }
    .template-template-03 .t03-main {
      padding: 20px 24px;
      overflow: visible;
      min-width: 0;
    }
    .template-template-03 .t03-photo-clip {
      width: 150px;
      height: 150px;
      overflow: hidden;
      border-radius: 12px;
    }
    .template-template-03 .t03-body-text {
      line-height: 1.5;
    }
    .template-template-03 .t03-body-text ul {
      list-style-type: disc;
      padding-left: 16px;
      margin: 4px 0 0;
    }
    .template-template-03 .t03-body-text ul li {
      margin: 0;
      padding: 0;
    }
    .template-template-03 .t03-section-title-outline {
      background: transparent !important;
      border: 1px solid ${accentColor};
      display: flex !important;
      align-items: center !important;
    }
    .template-template-03 .t03-skill-card {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .template-template-03 .t03-skill-label {
      display: block;
    }
    .template-template-03 .t03-skill-bar.t03-skill-bar-full {
      display: flex;
      gap: 6px;
      width: 100%;
    }
    .template-template-03 .t03-skill-bar.t03-skill-bar-full .t03-bar-seg {
      flex: 1;
      height: 1px;
      min-height: 1px;
      background: ${accentColor};
    }
    .template-template-03 .avoid-break {
      break-inside: avoid;
      page-break-inside: avoid;
    }
  `;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: css }} />
      <div className="t03-grid">
        <div className="t03-sidebar">
          {renderPhoto()}
          {renderIdentity()}
          <div style={{ height: 1, background: borderColor, marginBottom: 14 }} />
          {renderContacts()}
          {renderLanguages()}
          {renderSkills()}
          {renderMeta()}
        </div>
        <div className="t03-main">
          {renderSummary()}
          {renderExperience()}
          {renderEducation()}
          {renderCourses()}
          {renderHobbies()}
        </div>
      </div>
    </>
  );
});

Template03Renderer.displayName = 'Template03Renderer';

export { Template03Renderer };
export default Template03Renderer;
